import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Share2, Copy, Check, Facebook, MessageCircle, Send } from 'lucide-react';

const ShareDropdown = ({ isOpen, onClose, buttonRef, shareOptions, onOptionClick }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 192; // w-48 = 12rem = 192px
      
      // Calculate position
      let left = rect.right - dropdownWidth;
      let top = rect.bottom + 8;
      
      // Ensure dropdown doesn't go off screen
      if (left < 8) left = 8;
      if (left + dropdownWidth > window.innerWidth - 8) {
        left = window.innerWidth - dropdownWidth - 8;
      }
      
      setPosition({ top, left });
    }
  }, [isOpen, buttonRef]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <>
      <div 
        className="fixed inset-0 z-40" 
        onClick={onClose}
      />
      <div 
        className="fixed w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50"
        style={{ top: `${position.top}px`, left: `${position.left}px` }}
      >
        <div className="py-2 max-h-64 overflow-y-auto">
          {shareOptions.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.name}
                onClick={(e) => onOptionClick(e, option.action)}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3"
                type="button"
              >
                <Icon className={`w-4 h-4 ${option.color}`} />
                <span className="text-gray-700">{option.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    </>,
    document.body
  );
};

const ShareButton = ({ 
  url, 
  title, 
  description = '', 
  className = '',
  size = 'default',
  variant = 'button' // 'button' | 'icon'
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [copied, setCopied] = useState(false);
  const buttonRef = useRef(null);

  const shareUrl = `${window.location.origin}${url}`;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);
  
  // Create formatted share text with branding
  const shareText = `🎓 burnair Academy\n\n${title}\n\n${shareUrl}`;
  const encodedShareText = encodeURIComponent(shareText);

  const shareOptions = [
    {
      name: 'Copy Link',
      icon: copied ? Check : Copy,
      action: () => copyToClipboard(),
      color: copied ? 'text-green-600' : 'text-gray-600'
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      action: () => window.open(`https://wa.me/?text=${encodedShareText}`, '_blank'),
      color: 'text-green-600'
    },
    {
      name: 'Telegram',
      icon: Send,
      action: () => window.open(`https://t.me/share/url?url=${encodedUrl}&text=${encodeURIComponent(`🎓 burnair Academy - ${title}`)}`, '_blank'),
      color: 'text-blue-500'
    },
    {
      name: 'Facebook',
      icon: Facebook,
      action: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, '_blank'),
      color: 'text-blue-600'
    }
  ];

  const copyToClipboard = async () => {
    try {
      // Copy the formatted text instead of just the URL
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  const handleOptionClick = (e, action) => {
    e.preventDefault();
    e.stopPropagation();
    action();
    setShowDropdown(false);
  };

  const buttonSizes = {
    small: 'p-1',
    default: 'p-2',
    large: 'p-3'
  };

  const iconSizes = {
    small: 'w-4 h-4',
    default: 'w-5 h-5',
    large: 'w-6 h-6'
  };

  if (variant === 'icon') {
    return (
      <>
        <button
          ref={buttonRef}
          onClick={handleButtonClick}
          className={`
            ${buttonSizes[size]} rounded-full bg-gray-100 hover:bg-gray-200 
            text-gray-600 transition-colors ${className}
          `}
          title="Share"
          type="button"
        >
          <Share2 className={iconSizes[size]} />
        </button>
        <ShareDropdown 
          isOpen={showDropdown}
          onClose={() => setShowDropdown(false)}
          buttonRef={buttonRef}
          shareOptions={shareOptions}
          onOptionClick={handleOptionClick}
        />
      </>
    );
  }

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        className={`
          flex items-center space-x-2 px-4 py-2 bg-[#00A3E0] text-white 
          rounded-lg hover:bg-[#002855] transition-colors ${className}
        `}
        type="button"
      >
        <Share2 className="w-4 h-4" />
        <span>Share</span>
      </button>
      <ShareDropdown 
        isOpen={showDropdown}
        onClose={() => setShowDropdown(false)}
        buttonRef={buttonRef}
        shareOptions={shareOptions}
        onOptionClick={handleOptionClick}
      />
    </>
  );
};

export default ShareButton;